import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Bell, BellOff, LogOut, Trash2 } from 'lucide-react';
import { isChannelMuted } from '../utils/chatAlerts';

/**
 * Right-click menu for a conversation row in the chat sidebar. Same recipe as
 * the app's other context menus (FtpSidebar's RowMenu, DocumentsSidebar's
 * tree menu): portaled to `document.body` so the sidebar splitter's stacking
 * context can't clip it, `fixed` at the cursor, dismissed by an outside
 * mousedown or Escape.
 */

/**
 * Which items this conversation gets, and what they say. Pure and exported
 * because it is the whole behavioural surface of the menu — "does a DM offer
 * Leave" and "does a muted row say Unmute" are questions worth a test, and
 * neither is answerable while the answer lives inside JSX.
 *
 * `leave` is offered for group conversations and channels but NOT for DMs:
 * there is nothing to leave in a 1:1 — the membership IS the conversation, and
 * removing yourself from it is a different, destructive idea that this menu no
 * longer offers at all.
 */
/**
 * A DM is orphaned when nobody but you is left in it — which is what an
 * account deletion produces: `chat_channel_members.user_id` cascades away,
 * while the channel and the messages (sender nulled) stay behind.
 *
 * Membership is read from the channel the server sent, so a channel whose
 * members have not loaded reads as NOT orphaned and simply offers no delete.
 * The reverse default would put a destructive action on live conversations
 * during a slow load.
 */
export function isOrphanedDm(channel, currentUserId) {
  if (channel?.kind !== 'dm') return false;
  const members = channel.members || [];
  if (members.length === 0) return false;
  return members.every((m) => String(m?.user_id) === String(currentUserId));
}

/**
 * True when leaving this channel would DESTROY it — you are the only member
 * left, so there is nobody to hand it to.
 *
 * The server already behaves this way (`settle_roster` destroys a channel
 * whose roster empties, and enqueues the storage purge). This exists so the
 * confirmation can SAY so: "leave" and "leave, and permanently delete every
 * message and file in here" are the same click, and only one of them is what
 * the user thinks they are doing.
 *
 * A channel whose members have not loaded reads as NOT last — the warning
 * must never be the thing that is missing when it matters, but a false one on
 * a busy channel would teach people to click through it.
 */
export function leavingDestroysChannel(channel, currentUserId) {
  if (!channel || channel.kind === 'dm') return false;
  const members = channel.members || [];
  if (members.length === 0) return false;
  return members.every((m) => String(m?.user_id) === String(currentUserId));
}

export function chatRowMenuItems(channel, t, currentUserId) {
  if (!channel) return [];
  const muted = isChannelMuted(channel);
  const items = [
    {
      id: 'mute',
      label: muted ? t('chat.menu.unmute') : t('chat.menu.mute'),
      Icon: muted ? Bell : BellOff,
      danger: false,
    },
  ];
  if (channel.kind !== 'dm') {
    items.push({
      id: 'leave',
      label: t('chat.menu.leave'),
      Icon: LogOut,
      danger: true,
    });
  } else if (isOrphanedDm(channel, currentUserId)) {
    // Only for a DM whose other participant deleted their account. A live
    // conversation has no delete: it would destroy the other person's copy,
    // and the server refuses it anyway — offering a button that always fails
    // is worse than not offering one.
    items.push({
      id: 'delete-dm',
      label: t('chat.menu.deleteDm'),
      Icon: Trash2,
      danger: true,
    });
  }
  return items;
}

export default function ChatRowContextMenu({ x, y, channel, t, currentUserId, onSelect, onClose }) {
  const menuRef = useRef(null);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  const items = chatRowMenuItems(channel, t, currentUserId);
  if (!items.length || typeof document === 'undefined' || !document.body) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      role="menu"
      data-testid="chat-row-context-menu"
      className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      style={{ left: x, top: y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map(({ id, label, Icon, danger }) => (
        <button
          key={id}
          type="button"
          role="menuitem"
          className={`ctx-menu-btn${danger ? ' ctx-menu-btn--danger' : ''}`}
          onClick={() => {
            onClose();
            onSelect(id);
          }}
        >
          <Icon size={14} />
          <span>{label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}
